(ns manage.adset-preview-panel.db.core
  (:require [clojure.edn :as edn]
            [clojure.set :refer [rename-keys]]
            [common.content-function.core :as cfn]
            [common.db.interceptors :as interceptors]
            [goog.crypt.base64 :as base64]
            [manage.adset-preview-panel.db.templates :as templates]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as r.easy]))

(def initial-state {:adset-id nil
                    :adset-name nil
                    :schema-urns nil
                    :content-function nil
                    :sidebar-visible nil
                    :context-share false
                    :product-share false
                    :shared-context nil})

(defn- get-schema-urns
  "Get schema urns from content function and context-schemas"
  [content-function context-schemas]
  (-> content-function
      :data
      (cfn/add-default-aliases context-schemas)
      :context))

(defn product-selectors-with-product-ids
  [products]
  (let [selector-params (keys products)
        selector-param->product-ids #(for [product (get-in products [% :items])]
                                       (:id product))]
    (map #(hash-map % (selector-param->product-ids %))
         selector-params)))

(def ^:private forward-events
  [{:register :adset-preview.core/get-adset-preview-data-success
    :events #{:resource.adset-preview/get-data-success}
    :dispatch-to [:adset-preview.core/get-adset-preview-data-success]}
   {:register :adset-preview.core/context-set
    :events #{:context/set}
    :dispatch-to [:adset-preview.core/context-set]}
   {:register :adset-preview.core/content-function-missing
    :events #{:resource.adset-preview/get-data-failure}
    :dispatch-to [:adset-preview.core/open-missing-content-modal]}
   {:register :adset-preview.core/get-adset-preview-data-failure
    :events #{:resource.adset-preview/get-data-failure}
    :dispatch-to [:adset-preview.notifications/show-short :adset-preview-data-failure
                  {:type "error"
                   :message "Something went wrong fetching data for adset preview."}]}
   {:register :adset-preview.core/set-expiry-date
    :events #{:resource.auth/unsign-preview-token-success}
    :dispatch-to [:adset-preview.core/set-expiry-date]}])

;; Component initializing
(rf/reg-event-fx :adset-preview.core/init
  [(rf/inject-cofx :query-params)]
  (fn [{:keys [db] :as cofx} [_ mobile?]]
    {:db (assoc db :adset-preview.core
                (assoc initial-state :shared-context
                       (some-> (get-in cofx [:query-params :context])
                               base64/decodeString
                               edn/read-string)))
     :forward-events forward-events
     :dispatch-n [[:adset-preview.products/init]
                  [:adset-preview.templates/init]
                  [:adset-preview.variants/init]
                  [:adset-preview.content-function-version/init]
                  [:adset-preview.core/toggle-sidebar (when-not mobile? :context)]]}))

(rf/reg-event-fx :adset-preview.core/destroy
  (fn [{:keys [db]} _]
    {:db (dissoc db :adset-preview.core :resource.content-function-version/active-version-name)
     :forward-events [{:unregister :adset-preview.core/get-adset-preview-data-success}
                      {:unregister :adset-preview.core/get-adset-preview-data-failure}
                      {:unregister :adset-preview.core/content-function-missing}
                      {:unregister :adset-preview.core/product-selector-result}
                      {:unregister :adset-preview.core/checksum-content-functions}]
     :dispatch-n [[:adset-preview.templates/destroy]
                  [:adset-preview.variants/destroy]
                  [:adset-preview.content-function-version/destroy]
                  [:adset-preview.products/destroy]]}))

(rf/reg-event-fx :adset-preview.core/get-adset-preview-data-success
  [interceptors/forwarded-event
   interceptors/transform-keys->kebab-case]
  (fn [_ [_ {:keys [general content-function]}]]
    {:dispatch [:adset-preview.core/set-adset-preview
                (assoc general :content-function content-function)]}))

;; Set adset preview data
(rf/reg-event-fx :adset-preview.core/set-adset-preview
  (fn [{:keys [db]}
       [_ {:keys [content-function context-schemas revisions] :as adset-preview}]]
    (let [advertiser-id (get-in content-function [:meta :advertiser-id])
          schema-urns (get-schema-urns content-function context-schemas)
          templates (map templates/revision->template revisions)
          external-template (get-in db [:adset-preview.templates :external-template])
          templates* (cond-> templates
                       (some? external-template) (conj external-template))
          state (-> adset-preview
                    (dissoc :content-functions :revisions)
                    (rename-keys {:id :adset-id})
                    (assoc :schema-urns schema-urns))]
      {:db (update-in db [:adset-preview.core] merge state)
       :dispatch-n [[:context/set (assoc (:context db) :advertiser-id advertiser-id)]
                    [:adset-preview.products/init-product-selectors schema-urns]
                    [:adset-preview.templates/set-templates templates*]
                    [:adset-preview.variants/set-adset-preview]]})))

(rf/reg-event-fx :adset-preview.core/copy-share-link
  (fn [{:keys [db]} [_ share-link]]
    {:db (assoc-in db [:adset-preview.core :link-copied] :success)
     :dispatch-later [{:ms 3000 :dispatch [:adset-preview.core/reset-link-copied]}]
     :content->clipboard {:content share-link}}))

;; This event is dispatched on adset-preview/get-data-failure
(rf/reg-event-fx :adset-preview.core/open-missing-content-modal
  [interceptors/forwarded-event]
  (fn [_ [_ {:keys [status]}]]
    (when (= 404 status)
      {:dispatch [:modal/open {:id :missing-content-modal}]})))

(rf/reg-event-db :adset-preview.core/reset-link-copied
  (fn [db _]
    (update db :adset-preview.core dissoc :link-copied)))

(rf/reg-event-db :adset-preview.core/toggle-sidebar
  [(rf/path :adset-preview.core)]
  (fn [state [_ sidebar]]
    (assoc state :sidebar-visible sidebar)))

(rf/reg-event-db :adset-preview.core/toggle-context-share
  [(rf/path [:adset-preview.core :context-share])]
  (fn [context-share _]
    (not context-share)))

(rf/reg-event-db :adset-preview.core/toggle-products-share
  [(rf/path [:adset-preview.core :products-share])]
  (fn [products-share _]
    (not products-share)))

(rf/reg-sub :adset-preview.core/context-share
  (fn [db]
    (get-in db [:adset-preview.core :context-share])))

(rf/reg-sub :adset-preview.core/products-share
  (fn [db]
    (get-in db [:adset-preview.core :products-share])))

(rf/reg-sub :adset-preview.core/context-encoded
  :<- [:adset-preview.variants/value :selected-variant-context]
  (fn [context-json]
    (base64/encodeString context-json)))

;; currently only does the first product selector, as discussed with Martijn
;; we will need to remove the 'first' step if we switch to multiple products selectors
(rf/reg-sub :adset-preview.core/products-encoded
  :<- [:adset-preview.products/products-for-selectors+params]
  (fn [products-for-selectors]
    (->> products-for-selectors
         product-selectors-with-product-ids
         first
         base64/encodeString)))

(rf/reg-sub :adset-preview.core/share-url
  :<- [:url-context]
  :<- [:adset-preview.core/context-share]
  :<- [:adset-preview.core/products-share]
  :<- [:adset-preview.core/context-encoded]
  :<- [:adset-preview.core/products-encoded]
  :<- [:resource.content-function-version/active-version {:only-staged true}]
  (fn [[url-context context-share products-share context-encoded products-encoded active-stage-version] _]
    (let [url-params (cond-> {}
                       context-share (assoc :context context-encoded)
                       products-share (assoc :products products-encoded)
                       active-stage-version (assoc :stage (:name active-stage-version)))]
      (str js/window.location.origin
           (r.easy/href :common/shared-rules-preview url-context url-params)))))

(rf/reg-sub :adset-preview.core/value
  (fn [db [_ path default]]
    (get-in db [:adset-preview.core path] default)))

(rf/reg-event-fx :adset-preview.core/context-set
  [interceptors/forwarded-event]
  (fn [{:keys [db]} [_ {:keys [preview-token]}]]
    (let [preview-context (some-> preview-token
                                  js/decodeURIComponent
                                  base64/decodeString
                                  js/JSON.parse
                                  (js->clj :keywordize-keys true)
                                  :d
                                  js/JSON.parse
                                  (js->clj :keywordize-keys true))]
      {:db (assoc db :preview-context preview-context)
       :fx [[:dispatch [:resource.auth/unsign-preview-token {:token (:auth preview-context)}]]]})))

(rf/reg-sub :preview-context
  (fn [db _]
    (:preview-context db)))

(rf/reg-sub :preview-auth-token
  :<- [:preview-context]
  (fn [preview-context]
    (when-let [token (:auth preview-context)]
      (str "lemonpi " token))))

(rf/reg-event-db :adset-preview.core/set-expiry-date
  (fn [db [_ [_ unsigned-token]]]
    (assoc-in db [:adset-preview.core :expiry-date] (:exp unsigned-token))))

(rf/reg-sub :adset-preview.core/expiry-date
  (fn [db]
    (get-in db [:adset-preview.core :expiry-date])))

(rf/reg-sub :adset-preview.core/content-function
  (fn [db]
    (get-in db [:adset-preview.core :content-function])))

(rf/reg-sub :adset-preview.core/has-products-param
  :<- [:url-context]
  (fn [url-context]
    (some? (:products url-context))))
